import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getGanttData, updateTask, getSections, getDependencyAlerts } from '../api';
import { useTheme } from '../hooks/useTheme';

const getSectionPrefix = (section) => {
  if (!section || section === 'Unassigned') return 'T';
  const words = section.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase();
};

const generateTaskCode = (section, index) => {
  const prefix = getSectionPrefix(section);
  return `${prefix}-${String(index).padStart(3, '0')}`;
};

const ZOOM_LEVELS = [
  { key: 'day', label: 'Day', dayWidth: 36 },
  { key: 'week', label: 'Week', dayWidth: 16 },
  { key: 'month', label: 'Month', dayWidth: 5 },
  { key: 'quarter', label: 'Quarter', dayWidth: 1.8 },
];

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 52;
const LABEL_WIDTH = 280;

const STATUS_COLORS = {
  complete: { bar: '#16a34a', bg: '#16a34a', text: '#fff' },
  completed: { bar: '#16a34a', bg: '#16a34a', text: '#fff' },
  'in-progress': { bar: '#2563eb', bg: '#93c5fd', text: '#fff' },
  'in progress': { bar: '#2563eb', bg: '#93c5fd', text: '#fff' },
  'not-started': { bar: '#9ca3af', bg: '#e5e7eb', text: '#fff' },
  'not started': { bar: '#9ca3af', bg: '#e5e7eb', text: '#fff' },
};

// Determine color based on actual progress, not just status field
const getProgressColors = (task) => {
  const progress = task.progress || 0;
  const status = task.status?.toLowerCase() || 'not started';
  if (progress >= 100 || status === 'complete' || status === 'completed') {
    return STATUS_COLORS['complete'];
  }
  if (progress > 0) {
    return STATUS_COLORS['in-progress'];
  }
  return STATUS_COLORS['not started'];
};

const getStatusColors = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS['not started'];
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const daysBetween = (d1, d2) => {
  const a = new Date(d1 + 'T00:00:00');
  const b = new Date(d2 + 'T00:00:00');
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
};

const GanttTab = ({ projectId, refreshSummary, onTaskOpen, refreshKey }) => {
  const t = useTheme();
  const styles = getStyles(t);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1); // default 'week'
  const [expandedSections, setExpandedSections] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverTask, setDragOverTask] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [savedSections, setSavedSections] = useState([]);
  const [depAlerts, setDepAlerts] = useState({ alerts: [], flagged_task_ids: [], summary: {} });
  const [labelWidth, setLabelWidth] = useState(LABEL_WIDTH);
  const chartAreaRef = useRef(null);
  const svgRef = useRef(null);
  const labelColumnRef = useRef(null);
  const timeHeaderScrollRef = useRef(null);
  const resizingRef = useRef(false);
  const resizeStartRef = useRef({ x: 0, width: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingRef.current) return;
      const diff = e.clientX - resizeStartRef.current.x;
      const newWidth = Math.max(150, Math.min(600, resizeStartRef.current.width + diff));
      setLabelWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (resizingRef.current) {
        resizingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    resizeStartRef.current = { x: e.clientX, width: labelWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Only show loading spinner on initial load, not on refreshes (which reset scroll position)
        if (!initialLoadDoneRef.current) setLoading(true);
        const [data, secs, depAlertData] = await Promise.all([
          getGanttData(projectId),
          getSections(projectId),
          getDependencyAlerts(projectId).catch(() => ({ alerts: [], flagged_task_ids: [], summary: {} })),
        ]);
        setTasks(data.tasks || []);
        setSavedSections(secs || []);
        setDepAlerts(depAlertData);
        setError(null);
        initialLoadDoneRef.current = true;
      } catch (err) {
        setError(err.message || 'Failed to load Gantt data');
      } finally {
        setLoading(false);
      }
    };
    if (projectId) fetchData();
  }, [projectId, refreshKey]);

  // Compute date range with padding
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    if (!tasks.length) return { rangeStart: null, rangeEnd: null, totalDays: 0 };
    const dates = tasks.flatMap(t => {
      const d = [];
      if (t.start_date) d.push(new Date(t.start_date + 'T00:00:00'));
      if (t.end_date) d.push(new Date(t.end_date + 'T00:00:00'));
      return d;
    }).filter(d => !isNaN(d));
    if (!dates.length) return { rangeStart: null, rangeEnd: null, totalDays: 0 };

    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    // Add 14 days padding each side
    min.setDate(min.getDate() - 14);
    max.setDate(max.getDate() + 14);
    const total = Math.round((max - min) / (1000 * 60 * 60 * 24)) + 1;
    return { rangeStart: min, rangeEnd: max, totalDays: total };
  }, [tasks]);

  // Group by building_area_phase
  const groupedTasks = useMemo(() => {
    const groups = {};
    tasks.forEach(task => {
      const section = task.building_area_phase || 'Unassigned';
      if (!groups[section]) groups[section] = [];
      groups[section].push(task);
    });
    return groups;
  }, [tasks]);

  const sections = useMemo(() => {
    const savedNames = savedSections.map(s => s.section_name);
    const savedSet = new Set(savedNames);
    const taskSections = Object.keys(groupedTasks);
    const unsaved = taskSections.filter(n => !savedSet.has(n)).sort();
    return [...savedNames.filter(n => taskSections.includes(n) || savedSet.has(n)), ...unsaved];
  }, [groupedTasks, savedSections]);

  // Set of task IDs with dependency alerts for quick lookup
  const flaggedTaskSet = useMemo(() => new Set(depAlerts.flagged_task_ids || []), [depAlerts]);

  // Alert messages indexed by task ID for tooltips
  const alertsByTaskId = useMemo(() => {
    const map = {};
    for (const alert of (depAlerts.alerts || [])) {
      if (!map[alert.task_id]) map[alert.task_id] = [];
      map[alert.task_id].push(alert);
      if (alert.related_task_id) {
        if (!map[alert.related_task_id]) map[alert.related_task_id] = [];
        // Avoid duplicates — only add if not already referencing this alert
        if (!map[alert.related_task_id].some(a => a.message === alert.message)) {
          map[alert.related_task_id].push(alert);
        }
      }
    }
    return map;
  }, [depAlerts]);

  // Set of violated dependency pairs for coloring arrows red: "taskId-predId"
  const violatedDepPairs = useMemo(() => {
    const pairs = new Set();
    for (const alert of (depAlerts.alerts || [])) {
      if (alert.type === 'date_violation' && alert.task_id && alert.related_task_id) {
        pairs.add(`${alert.task_id}-${alert.related_task_id}`);
      }
    }
    return pairs;
  }, [depAlerts]);

  // Build a flat list of visible rows for arrow drawing
  const visibleRows = useMemo(() => {
    const rows = [];
    sections.forEach(section => {
      rows.push({ type: 'section', section });
      if (expandedSections[section] !== false) {
        groupedTasks[section].forEach(task => {
          rows.push({ type: 'task', task, section });
        });
      }
    });
    return rows;
  }, [sections, groupedTasks, expandedSections]);

  // Task ID -> row index map (for arrow endpoints)
  const taskRowIndex = useMemo(() => {
    const map = {};
    visibleRows.forEach((row, idx) => {
      if (row.type === 'task') map[row.task.id] = idx;
    });
    return map;
  }, [visibleRows]);

  const zoom = ZOOM_LEVELS[zoomLevel];
  // Ensure chart is wide enough to fill the viewport (estimate ~1800px max screen)
  const minChartPx = 1800;
  const minDaysForViewport = Math.ceil(minChartPx / zoom.dayWidth);
  const effectiveTotalDays = Math.max(totalDays, minDaysForViewport);
  const chartWidth = effectiveTotalDays * zoom.dayWidth;
  // Extended range end for time headers/grid lines to fill the extra space
  const effectiveRangeEnd = useMemo(() => {
    if (!rangeStart) return rangeEnd;
    const d = new Date(rangeStart);
    d.setDate(d.getDate() + effectiveTotalDays);
    return d;
  }, [rangeStart, rangeEnd, effectiveTotalDays]);

  const getDayOffset = useCallback((dateStr) => {
    if (!rangeStart || !dateStr) return 0;
    const d = new Date(dateStr + 'T00:00:00');
    return Math.round((d - rangeStart) / (1000 * 60 * 60 * 24));
  }, [rangeStart]);

  const getBarX = useCallback((dateStr) => {
    return getDayOffset(dateStr) * zoom.dayWidth;
  }, [getDayOffset, zoom.dayWidth]);

  const getBarWidth = useCallback((startDate, endDate) => {
    const days = daysBetween(startDate, endDate) + 1;
    return Math.max(days * zoom.dayWidth, 4);
  }, [zoom.dayWidth]);

  // Today line position
  const todayOffset = useMemo(() => {
    if (!rangeStart) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOff = Math.round((today - rangeStart) / (1000 * 60 * 60 * 24));
    if (dayOff < 0 || dayOff > effectiveTotalDays) return null;
    return dayOff * zoom.dayWidth;
  }, [rangeStart, effectiveTotalDays, zoom.dayWidth]);

  // Generate time header markers
  const timeHeaders = useMemo(() => {
    if (!rangeStart || !effectiveRangeEnd) return { primary: [], secondary: [] };

    const eDays = effectiveTotalDays;
    const eEnd = effectiveRangeEnd;
    const primary = [];
    const secondary = [];

    if (zoom.key === 'day' || zoom.key === 'week') {
      // Primary: months, Secondary: weeks or days
      const cur = new Date(rangeStart);
      cur.setDate(1);
      if (cur < rangeStart) cur.setMonth(cur.getMonth());

      while (cur <= eEnd) {
        const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
        const nextMonth = new Date(cur);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        const endOff = Math.round((nextMonth - rangeStart) / (1000 * 60 * 60 * 24));
        primary.push({
          label: cur.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          left: Math.max(0, dayOff) * zoom.dayWidth,
          width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
        });
        cur.setMonth(cur.getMonth() + 1);
      }

      if (zoom.key === 'day') {
        // Show individual days
        for (let i = 0; i < eDays; i++) {
          const d = new Date(rangeStart);
          d.setDate(d.getDate() + i);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          secondary.push({
            label: d.getDate().toString(),
            left: i * zoom.dayWidth,
            width: zoom.dayWidth,
            isWeekend,
          });
        }
      } else {
        // Show week starts
        const d = new Date(rangeStart);
        // Move to Monday
        const dayOfWeek = d.getDay();
        const toMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 8 - dayOfWeek);
        d.setDate(d.getDate() + toMonday);
        while (d <= eEnd) {
          const dayOff = Math.round((d - rangeStart) / (1000 * 60 * 60 * 24));
          secondary.push({
            label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            left: dayOff * zoom.dayWidth,
            width: 7 * zoom.dayWidth,
          });
          d.setDate(d.getDate() + 7);
        }
      }
    } else if (zoom.key === 'month') {
      // Primary: quarters, Secondary: months
      const cur = new Date(rangeStart);
      cur.setDate(1);
      const quarter = Math.floor(cur.getMonth() / 3);
      cur.setMonth(quarter * 3);

      while (cur <= eEnd) {
        const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
        const nextQ = new Date(cur);
        nextQ.setMonth(nextQ.getMonth() + 3);
        const endOff = Math.round((nextQ - rangeStart) / (1000 * 60 * 60 * 24));
        const q = Math.floor(cur.getMonth() / 3) + 1;
        primary.push({
          label: `Q${q} ${cur.getFullYear()}`,
          left: Math.max(0, dayOff) * zoom.dayWidth,
          width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
        });
        cur.setMonth(cur.getMonth() + 3);
      }

      const m = new Date(rangeStart);
      m.setDate(1);
      while (m <= eEnd) {
        const dayOff = Math.round((m - rangeStart) / (1000 * 60 * 60 * 24));
        const nextM = new Date(m);
        nextM.setMonth(nextM.getMonth() + 1);
        const endOff = Math.round((nextM - rangeStart) / (1000 * 60 * 60 * 24));
        secondary.push({
          label: m.toLocaleDateString('en-US', { month: 'short' }),
          left: Math.max(0, dayOff) * zoom.dayWidth,
          width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
        });
        m.setMonth(m.getMonth() + 1);
      }
    } else {
      // Quarter view: Primary: years, Secondary: quarters
      const cur = new Date(rangeStart);
      cur.setMonth(0, 1);
      while (cur <= eEnd) {
        const dayOff = Math.round((cur - rangeStart) / (1000 * 60 * 60 * 24));
        const nextY = new Date(cur);
        nextY.setFullYear(nextY.getFullYear() + 1);
        const endOff = Math.round((nextY - rangeStart) / (1000 * 60 * 60 * 24));
        primary.push({
          label: cur.getFullYear().toString(),
          left: Math.max(0, dayOff) * zoom.dayWidth,
          width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
        });
        cur.setFullYear(cur.getFullYear() + 1);
      }

      const q = new Date(rangeStart);
      q.setDate(1);
      const qStart = Math.floor(q.getMonth() / 3);
      q.setMonth(qStart * 3);
      while (q <= eEnd) {
        const dayOff = Math.round((q - rangeStart) / (1000 * 60 * 60 * 24));
        const nextQ = new Date(q);
        nextQ.setMonth(nextQ.getMonth() + 3);
        const endOff = Math.round((nextQ - rangeStart) / (1000 * 60 * 60 * 24));
        const qNum = Math.floor(q.getMonth() / 3) + 1;
        secondary.push({
          label: `Q${qNum}`,
          left: Math.max(0, dayOff) * zoom.dayWidth,
          width: (Math.min(endOff, eDays) - Math.max(dayOff, 0)) * zoom.dayWidth,
        });
        q.setMonth(q.getMonth() + 3);
      }
    }

    return { primary, secondary };
  }, [rangeStart, effectiveRangeEnd, effectiveTotalDays, zoom]);

  // Dependency arrows
  const arrows = useMemo(() => {
    const result = [];
    visibleRows.forEach((row) => {
      if (row.type !== 'task' || !row.task.dependencies?.length) return;
      const task = row.task;
      const toIdx = taskRowIndex[task.id];
      if (toIdx === undefined) return;

      task.dependencies.forEach(dep => {
        const fromIdx = taskRowIndex[dep.predecessor_task_id];
        if (fromIdx === undefined) return;
        const predRow = visibleRows[fromIdx];
        if (!predRow || predRow.type !== 'task') return;
        const pred = predRow.task;

        const depType = dep.dependency_type || 'FS';
        let startX, startY, endX, endY;

        const predBarX = getBarX(pred.start_date);
        const predBarEnd = predBarX + getBarWidth(pred.start_date, pred.end_date);
        const taskBarX = getBarX(task.start_date);
        const taskBarEnd = taskBarX + getBarWidth(task.start_date, task.end_date);

        const rowCenterY = (idx) => idx * ROW_HEIGHT + ROW_HEIGHT / 2;

        if (depType === 'FS') {
          startX = predBarEnd;
          endX = taskBarX;
        } else if (depType === 'SS') {
          startX = predBarX;
          endX = taskBarX;
        } else if (depType === 'FF') {
          startX = predBarEnd;
          endX = taskBarEnd;
        } else if (depType === 'SF') {
          startX = predBarX;
          endX = taskBarEnd;
        } else {
          startX = predBarEnd;
          endX = taskBarX;
        }

        startY = rowCenterY(fromIdx);
        endY = rowCenterY(toIdx);

        result.push({ startX, startY, endX, endY, depType, taskId: task.id, predId: dep.predecessor_task_id });
      });
    });
    return result;
  }, [visibleRows, taskRowIndex, getBarX, getBarWidth]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: prev[section] === false ? true : false }));
  };

  const scrollToToday = () => {
    if (chartAreaRef.current && todayOffset !== null) {
      chartAreaRef.current.scrollLeft = todayOffset - chartAreaRef.current.clientWidth / 2;
    }
  };

  // Auto-scroll to today on initial load only (not on data refreshes)
  const hasScrolledRef = useRef(false);
  useEffect(() => {
    if (!loading && todayOffset !== null && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      setTimeout(scrollToToday, 100);
    }
  }, [loading, todayOffset]);

  // Also scroll when zoom level changes
  useEffect(() => {
    if (!loading && todayOffset !== null && hasScrolledRef.current) {
      setTimeout(scrollToToday, 100);
    }
  }, [zoomLevel]);

  // ============ DRAG AND DROP ============
  const handleDragStart = (e, task, section) => {
    setDraggedTask(task);
    setDragSection(section);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTask(null);
    setDragOverTask(null);
    setDragSection(null);
  };

  const handleDragOver = (e, task) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== dragOverTask) {
      setDragOverTask(task.id);
    }
  };

  const handleDrop = async (e, targetTask, section) => {
    e.preventDefault();
    if (!draggedTask || draggedTask.id === targetTask.id) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    // Only allow reorder within same section
    if (dragSection !== section) {
      setDraggedTask(null);
      setDragOverTask(null);
      return;
    }

    const sectionTasks = [...(groupedTasks[section] || [])];
    const dragIdx = sectionTasks.findIndex(t => t.id === draggedTask.id);
    const dropIdx = sectionTasks.findIndex(t => t.id === targetTask.id);

    if (dragIdx === -1 || dropIdx === -1) return;

    // Reorder: remove dragged, insert at drop position
    const [moved] = sectionTasks.splice(dragIdx, 1);
    sectionTasks.splice(dropIdx, 0, moved);

    // Build new codes for the reordered section
    const updates = sectionTasks.map((task, idx) => ({
      id: task.id,
      newCode: generateTaskCode(section, idx + 1)
    }));

    // Optimistic UI update
    const otherTasks = tasks.filter(t => (t.building_area_phase || 'Unassigned') !== section);
    const reorderedSectionTasks = sectionTasks.map((t, idx) => ({
      ...t,
      code: updates[idx].newCode
    }));
    setTasks([...otherTasks, ...reorderedSectionTasks]);

    setDraggedTask(null);
    setDragOverTask(null);

    // Persist to backend
    for (const upd of updates) {
      try {
        await updateTask(upd.id, { task_code: upd.newCode });
      } catch (err) {
        console.error('Error updating task code:', err);
      }
    }
  };

  // Sync horizontal scroll between chart area and sticky time header
  const handleChartScroll = useCallback(() => {
    if (chartAreaRef.current && timeHeaderScrollRef.current) {
      timeHeaderScrollRef.current.scrollLeft = chartAreaRef.current.scrollLeft;
    }
  }, []);


  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <span style={{ color: t.textSecondary, fontSize: '14px' }}>Loading Gantt chart...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>Error: {error}</div>
      </div>
    );
  }

  if (!tasks.length) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '40px', textAlign: 'center', color: t.textSecondary }}>
          No tasks with dates to display. Add start and finish dates to your tasks to see them here.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Sticky top block: toolbar + legend + column/time headers */}
      <div style={styles.stickyTopBlock}>
        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.toolbarLeft}>
            <div>
              <span style={styles.title}>Schedule Gantt Chart</span>
              <div style={{ fontSize: '12px', color: t.textSecondary, marginTop: '2px' }}>
                {tasks.filter(t => t.start_date && t.end_date).length} tasks with dates · Drag rows to reorder
              </div>
            </div>
          </div>
          <div style={styles.toolbarRight}>
            <button style={styles.todayBtn} onClick={scrollToToday} title="Scroll to today">
              Today
            </button>
            <div style={styles.zoomGroup}>
              {ZOOM_LEVELS.map((lvl, i) => (
                <button
                  key={lvl.key}
                  style={{
                    ...styles.zoomBtn,
                    ...(i === zoomLevel ? styles.zoomBtnActive : {}),
                    borderRadius: i === 0 ? '6px 0 0 6px' : i === ZOOM_LEVELS.length - 1 ? '0 6px 6px 0' : '0',
                  }}
                  onClick={() => setZoomLevel(i)}
                >
                  {lvl.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={styles.legend}>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#9ca3af' }} /><span>Not Started</span></div>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#2563eb' }} /><span>In Progress</span></div>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: '#16a34a' }} /><span>Complete</span></div>
          <div style={styles.legendItem}><div style={{ width: '16px', height: '2px', backgroundColor: '#ef4444' }} /><span>Today</span></div>
          <div style={styles.legendItem}>
            <svg width="20" height="10"><path d="M0,5 L14,5" stroke="#94a3b8" strokeWidth="1.5" /><path d="M10,2 L16,5 L10,8 Z" fill="#94a3b8" /></svg>
            <span>Dependency</span>
          </div>
        </div>

        {/* Dependency alerts banner */}
        {depAlerts.summary?.total > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '6px 20px',
            backgroundColor: depAlerts.summary.critical > 0 ? '#fef2f2' : '#fffbeb',
            borderBottom: `1px solid ${depAlerts.summary.critical > 0 ? '#fecaca' : '#fde68a'}`,
            fontSize: '13px',
            color: depAlerts.summary.critical > 0 ? '#dc2626' : '#d97706',
          }}>
            <span style={{ fontSize: '15px' }}>&#9888;</span>
            <span style={{ fontWeight: 600 }}>
              {depAlerts.summary.total} scheduling {depAlerts.summary.total === 1 ? 'alert' : 'alerts'}
            </span>
            {depAlerts.summary.critical > 0 && (
              <span style={{ fontSize: '12px', opacity: 0.85 }}>
                ({depAlerts.summary.critical} critical{depAlerts.summary.warning > 0 ? `, ${depAlerts.summary.warning} warning` : ''})
              </span>
            )}
          </div>
        )}

        {/* Column + Time Headers Row */}
        <div style={styles.stickyHeaderRow}>
        {/* Label column header */}
        <div style={{ ...styles.labelHeaderCell, width: labelWidth, minWidth: labelWidth }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Task Name</span>
        </div>
        {/* Time header (scroll-synced with chart) */}
        <div ref={timeHeaderScrollRef} style={styles.timeHeaderScroll}>
          <div style={{ width: chartWidth, height: HEADER_HEIGHT, position: 'relative' }}>
            {/* Primary row */}
            <div style={styles.timeHeaderPrimary}>
              {timeHeaders.primary.map((h, i) => (
                <div key={i} style={{ position: 'absolute', left: h.left, width: h.width, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: `1px solid ${t.tableBorder}`, fontSize: '12px', fontWeight: 600, color: t.text }}>
                  {h.label}
                </div>
              ))}
            </div>
            {/* Secondary row */}
            <div style={styles.timeHeaderSecondary}>
              {timeHeaders.secondary.map((h, i) => (
                <div key={i} style={{
                  position: 'absolute', left: h.left, width: h.width, height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRight: `1px solid ${t.tableBorder}`,
                  fontSize: '11px', color: t.textSecondary, fontWeight: 500,
                  backgroundColor: h.isWeekend ? t.bgAlt : 'transparent',
                }}>
                  {h.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Chart Area (labels + bars side by side) */}
      <div style={styles.chartWrapper}>
        {/* Left labels */}
        <div ref={labelColumnRef} style={{ ...styles.labelColumn, width: labelWidth, minWidth: labelWidth }}>
          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            style={{
              position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px',
              cursor: 'col-resize', zIndex: 25, backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#93c5fd'}
            onMouseLeave={(e) => { if (!resizingRef.current) e.currentTarget.style.backgroundColor = 'transparent'; }}
          />
          {/* Rows */}
          {visibleRows.map((row, idx) => {
            if (row.type === 'section') {
              const sectionTasks = groupedTasks[row.section];
              const taskCount = sectionTasks.length;
              const avgProgress = taskCount > 0 ? Math.round(sectionTasks.reduce((sum, t) => sum + (t.progress || 0), 0) / taskCount) : 0;
              const isExpanded = expandedSections[row.section] !== false;
              return (
                <div key={`sec-${row.section}`} style={styles.sectionLabel} onClick={() => toggleSection(row.section)}>
                  <span style={styles.sectionArrow}>{isExpanded ? '▾' : '▸'}</span>
                  <span style={styles.sectionText}>{row.section}</span>
                  <span style={styles.sectionCount}>{taskCount}</span>
                  {/* Mini progress bar */}
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '48px', height: '4px', backgroundColor: t.cardBorder, borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${avgProgress}%`, height: '100%', backgroundColor: avgProgress >= 100 ? '#059669' : '#2563eb', borderRadius: '2px' }} />
                    </div>
                    <span style={{ fontSize: '11px', color: t.textSecondary, fontWeight: 500, minWidth: '28px' }}>{avgProgress}%</span>
                  </div>
                </div>
              );
            }
            const task = row.task;
            const sc = getProgressColors(task);
            const isDragOver = dragOverTask === task.id;
            return (
              <div
                key={`task-${task.id}`}
                draggable
                onDragStart={(e) => handleDragStart(e, task, row.section)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, task)}
                onDrop={(e) => handleDrop(e, task, row.section)}
                onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                style={{
                  ...styles.taskLabel,
                  backgroundColor: draggedTask?.id === task.id ? '#eff6ff' : idx % 2 === 0 ? t.bg : t.bgAlt,
                  borderTop: isDragOver ? '2px solid #3b82f6' : '1px solid transparent',
                  cursor: 'grab',
                }}
              >
                {/* Drag grip */}
                <span style={styles.dragGrip}>⠿</span>
                {task.code && <span style={styles.taskCode}>{task.code}</span>}
                <span style={styles.taskName} title={task.name}>{task.name}</span>
                {!!task.is_milestone && <span style={{ fontSize: '10px', color: '#7c3aed' }}>&#9670;</span>}
                {flaggedTaskSet.has(task.id) && (
                  <span
                    title={alertsByTaskId[task.id]?.map(a => a.message).join('\n') || 'Scheduling alert'}
                    style={{
                      marginLeft: '4px',
                      fontSize: '13px',
                      cursor: 'help',
                      color: alertsByTaskId[task.id]?.some(a => a.severity === 'critical') ? '#dc2626' : '#d97706',
                    }}
                  >&#9888;</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Scrollable chart */}
        <div ref={chartAreaRef} style={styles.chartScroll} onScroll={handleChartScroll}>
          <div style={{ width: chartWidth, minHeight: visibleRows.length * ROW_HEIGHT, position: 'relative' }}>

            {/* Grid lines */}
            {timeHeaders.secondary.map((h, i) => (
              <div key={`gl-${i}`} style={{
                position: 'absolute', left: h.left, top: 0, bottom: 0,
                width: '1px', backgroundColor: t.bgAlt, zIndex: 0,
              }} />
            ))}

            {/* Today line */}
            {todayOffset !== null && (
              <>
                <div style={{
                  position: 'absolute', left: todayOffset, top: 0, bottom: 0,
                  width: '2px', backgroundColor: '#ef4444', zIndex: 10, pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute', left: todayOffset - 22, top: 2,
                  backgroundColor: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 600,
                  padding: '2px 6px', borderRadius: '3px', zIndex: 11, pointerEvents: 'none',
                  whiteSpace: 'nowrap',
                }}>
                  Today
                </div>
              </>
            )}

            {/* Rows background + bars */}
            {visibleRows.map((row, idx) => {
              const yTop = idx * ROW_HEIGHT;

              if (row.type === 'section') {
                return (
                  <div key={`sec-bg-${row.section}`} style={{
                    position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT,
                    backgroundColor: t.bgAlt, borderTop: `1px solid ${t.tableBorder}`, borderBottom: `1px solid ${t.tableBorder}`,
                  }} />
                );
              }

              const task = row.task;
              if (!task.start_date || !task.end_date) return null;

              const barX = getBarX(task.start_date);
              const barW = getBarWidth(task.start_date, task.end_date);
              const barY = yTop + 8;
              const barH = ROW_HEIGHT - 16;
              const hasDepAlert = flaggedTaskSet.has(task.id);
              const isCriticalAlert = hasDepAlert && alertsByTaskId[task.id]?.some(a => a.severity === 'critical');
              const sc = hasDepAlert
                ? { bar: '#dc2626', bg: isCriticalAlert ? '#fca5a5' : '#fde68a', text: '#fff' }
                : getProgressColors(task);
              const progress = task.progress || 0;
              const progressW = (barW * progress) / 100;

              return (
                <div key={`bar-${task.id}`}>
                  {/* Row background */}
                  <div style={{
                    position: 'absolute', left: 0, top: yTop, width: '100%', height: ROW_HEIGHT,
                    backgroundColor: hasDepAlert
                      ? (isCriticalAlert ? 'rgba(220, 38, 38, 0.06)' : 'rgba(217, 119, 6, 0.06)')
                      : (idx % 2 === 0 ? 'transparent' : t.bgAlt),
                    borderBottom: `1px solid ${t.tableBorder}`,
                  }} />

                  {task.is_milestone ? (
                    // Milestone diamond
                    <div
                      style={{
                        position: 'absolute', left: barX - 8, top: barY,
                        width: 0, height: 0,
                        borderLeft: '10px solid transparent', borderRight: '10px solid transparent',
                        borderBottom: `10px solid ${sc.bar}`, borderTop: `10px solid ${sc.bar}`,
                        cursor: 'pointer', zIndex: 5,
                      }}
                      onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, task })}
                      onMouseLeave={() => setTooltip(null)}
                      onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                    />
                  ) : (
                    // Normal bar + percentage label to the left
                    <>
                      <span style={{
                        position: 'absolute', left: barX - 4, top: barY,
                        transform: 'translateX(-100%)',
                        fontSize: '11px', fontWeight: 600, color: t.text,
                        lineHeight: `${barH}px`, whiteSpace: 'nowrap', zIndex: 6,
                      }}>
                        {progress}%
                      </span>
                      <div
                        style={{
                          position: 'absolute', left: barX, top: barY, width: barW, height: barH,
                          backgroundColor: sc.bg, borderRadius: '4px', overflow: 'hidden',
                          cursor: 'pointer', zIndex: 5,
                          border: hasDepAlert ? '2px solid #dc2626' : `2px solid ${t.heading}`,
                        }}
                        onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, task })}
                        onMouseLeave={() => setTooltip(null)}
                        onDoubleClick={() => onTaskOpen && onTaskOpen(task.id)}
                      >
                        {/* Progress fill */}
                        <div style={{
                          position: 'absolute', left: 0, top: 0, height: '100%',
                          width: progressW, backgroundColor: sc.bar,
                          borderRadius: progress >= 100 ? '4px' : '4px 0 0 4px',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {/* Dependency arrows SVG */}
            <svg
              ref={svgRef}
              style={{ position: 'absolute', top: 0, left: 0, width: chartWidth, height: visibleRows.length * ROW_HEIGHT, pointerEvents: 'none', zIndex: 8 }}
            >
              <defs>
                <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#94a3b8" />
                </marker>
                <marker id="arrowhead-red" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                  <path d="M0,0 L8,3 L0,6 Z" fill="#dc2626" />
                </marker>
              </defs>
              {arrows.map((a, i) => {
                const dx = a.endX - a.startX;
                const dy = a.endY - a.startY;
                const absDy = Math.abs(dy);
                let path;

                if (Math.abs(dx) < 20) {
                  const offsetX = -20;
                  path = `M${a.startX},${a.startY} C${a.startX + offsetX},${a.startY + dy * 0.3} ${a.endX + offsetX},${a.endY - dy * 0.3} ${a.endX},${a.endY}`;
                } else if (dx > 20) {
                  const cpOffset = Math.min(Math.abs(dx) * 0.4, 60);
                  path = `M${a.startX},${a.startY} C${a.startX + cpOffset},${a.startY} ${a.endX - cpOffset},${a.endY} ${a.endX},${a.endY}`;
                } else {
                  const dropX = Math.min(a.startX, a.endX) - 25;
                  const belowY = Math.max(a.startY, a.endY) + ROW_HEIGHT * 0.4;
                  path = `M${a.startX},${a.startY} Q${a.startX},${belowY} ${dropX},${belowY} Q${dropX},${a.endY} ${a.endX},${a.endY}`;
                }

                // Check if this specific dependency link is violated
                const isViolated = violatedDepPairs.has(`${a.taskId}-${a.predId}`);

                return (
                  <path
                    key={i}
                    d={path}
                    fill="none"
                    stroke={isViolated ? '#dc2626' : '#94a3b8'}
                    strokeWidth={isViolated ? 2.5 : 1.5}
                    strokeDasharray={isViolated ? '0' : '6,3'}
                    markerEnd={isViolated ? 'url(#arrowhead-red)' : 'url(#arrowhead)'}
                    opacity={isViolated ? 0.9 : 0.6}
                  />
                );
              })}
            </svg>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 10,
          backgroundColor: t.heading, color: '#fff', padding: '10px 14px',
          borderRadius: '8px', fontSize: '12px', zIndex: 9999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)', pointerEvents: 'none',
          maxWidth: '280px', lineHeight: '1.5',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tooltip.task.name}</div>
          {tooltip.task.code && <div style={{ color: t.textSecondary, marginBottom: '4px' }}>{tooltip.task.code}</div>}
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
            <div>
              <span style={{ color: t.textSecondary }}>Start: </span>
              <span>{formatDate(tooltip.task.start_date)}</span>
            </div>
            <div>
              <span style={{ color: t.textSecondary }}>End: </span>
              <span>{formatDate(tooltip.task.end_date)}</span>
            </div>
          </div>
          <div style={{ marginTop: '4px' }}>
            <span style={{ color: t.textSecondary }}>Progress: </span>
            <span style={{ fontWeight: 600 }}>{tooltip.task.progress || 0}%</span>
          </div>
          {tooltip.task.duration > 0 && (
            <div style={{ marginTop: '2px' }}>
              <span style={{ color: t.textSecondary }}>Duration: </span>
              <span>{tooltip.task.duration} days</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

const getStyles = (t) => ({
  container: {
    backgroundColor: t.bg,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    maxWidth: '100%',
    width: '100%',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  loadingWrap: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
    padding: '60px 0',
  },
  spinner: {
    width: '20px', height: '20px', border: `3px solid ${t.cardBorder}`, borderTopColor: '#2563eb',
    borderRadius: '50%', animation: 'spin 1s linear infinite',
  },
  stickyTopBlock: {
    position: 'sticky', top: 0, zIndex: 30, backgroundColor: t.bg,
  },
  toolbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', borderBottom: `1px solid ${t.tableBorder}`,
  },
  toolbarLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  title: { fontSize: '16px', fontWeight: 700, color: t.heading },
  taskCount: {
    fontSize: '12px', fontWeight: 500, color: t.textSecondary,
    backgroundColor: t.bgAlt, padding: '2px 8px', borderRadius: '10px',
  },
  todayBtn: {
    padding: '6px 14px', fontSize: '13px', fontWeight: 500, color: '#ef4444',
    backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
    cursor: 'pointer',
  },
  zoomGroup: { display: 'flex' },
  zoomBtn: {
    padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: t.textSecondary,
    backgroundColor: t.bgAlt, border: `1px solid ${t.cardBorder}`,
    cursor: 'pointer', borderRadius: 0,
  },
  zoomBtnActive: {
    backgroundColor: '#2563eb', color: '#fff', borderColor: '#2563eb',
  },
  chartWrapper: {
    display: 'flex', position: 'relative',
    width: '100%', maxWidth: '100%',
  },
  stickyHeaderRow: {
    display: 'flex',
    borderBottom: `1px solid ${t.tableBorder}`, backgroundColor: t.bgAlt,
  },
  labelHeaderCell: {
    display: 'flex', alignItems: 'flex-end', padding: '0 16px 8px',
    height: HEADER_HEIGHT, boxSizing: 'border-box',
    borderRight: `2px solid ${t.tableBorder}`, flexShrink: 0,
  },
  timeHeaderScroll: {
    flex: 1, overflow: 'hidden', height: HEADER_HEIGHT,
  },
  labelColumn: {
    width: LABEL_WIDTH, minWidth: LABEL_WIDTH, borderRight: `2px solid ${t.tableBorder}`,
    backgroundColor: t.bg, zIndex: 20, flexShrink: 0, position: 'relative',
  },
  sectionLabel: {
    display: 'flex', alignItems: 'center', height: ROW_HEIGHT, padding: '0 16px',
    backgroundColor: t.bgAlt, borderTop: `1px solid ${t.tableBorder}`, borderBottom: `1px solid ${t.tableBorder}`,
    cursor: 'pointer', userSelect: 'none',
  },
  sectionArrow: { fontSize: '12px', color: t.textSecondary, marginRight: '6px', width: '12px', flexShrink: 0 },
  sectionText: { fontSize: '13px', fontWeight: 600, color: t.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sectionCount: {
    marginLeft: '6px', fontSize: '11px', fontWeight: 500, color: t.textSecondary,
    backgroundColor: t.cardBorder, padding: '1px 6px', borderRadius: '8px', flexShrink: 0,
  },
  taskLabel: {
    display: 'flex', alignItems: 'center', height: ROW_HEIGHT, padding: '0 10px 0 28px',
    borderBottom: `1px solid ${t.tableBorder}`, gap: '8px', overflow: 'hidden',
  },
  taskName: {
    fontSize: '13px', fontWeight: 400, color: t.text, flex: 1, minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  taskCode: {
    fontSize: '12px', color: t.textSecondary, fontWeight: 500, flexShrink: 0,
    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  },
  dragGrip: {
    color: t.textSecondary, fontSize: '14px', cursor: 'grab', flexShrink: 0,
    userSelect: 'none', lineHeight: 1, marginRight: '4px',
    letterSpacing: '-1px',
  },
  chartScroll: { flex: 1, overflowX: 'auto' },
  timeHeaderPrimary: { position: 'relative', height: '26px', borderBottom: `1px solid ${t.tableBorder}` },
  timeHeaderSecondary: { position: 'relative', height: '25px' },
  legend: {
    display: 'flex', alignItems: 'center', gap: '20px', padding: '10px 20px',
    borderBottom: `1px solid ${t.tableBorder}`, fontSize: '12px', color: t.textSecondary,
    backgroundColor: t.bg,
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px' },
  legendDot: { width: '10px', height: '10px', borderRadius: '3px' },
});

export default GanttTab;
